import { AloftPrice, isUndefined } from '@aloft/toolkit-shared';
import { Input, Flex as WebFlex } from '@aloft/toolkit-sui';

import { chunk } from 'lodash';
import { useState } from 'react';

import { PDFOnly } from '../../../summary/components/PDFOnly.tsx';
import { Typography } from '../../../summary/components/Typography.tsx';
import { WebOnly } from '../../../summary/components/WebOnly.tsx';
import { SummarySection } from '../SummarySection.tsx';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import { COMPS_PER_TABLE } from '../constants.js';
import { Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { AdjustmentBreakdownSiteValueTable } from './AdjustmentBreakdownSiteValueTable.tsx';
import { PairedCostSettingsBreakdown } from './PairedCostSettingsBreakdown.tsx';
import { hasAdjustments } from './hasAdjustments.ts';

export const AdjustmentBreakdownSiteValue = (props: Tkv2PairedCostSummaryInterfaceV2) => {
  const compChunks = chunk(props.compSequence, COMPS_PER_TABLE);
  const commentaryProps = props.exportableCommentaries.site;
  const [commentary, setCommentary] = useState(commentaryProps.commentary);

  const subjectSiteVal = props.results.subject?.totals?.site.marketAdjusted.siteValue.totalCost;
  /**
   * Skip rendering the site breakdown table if both are true:
   * 1. No site adjustments exist
   * 2. Subject site value is zero (either hasn't been costed or manually entered as "0")
   *
   * Currently no better way to check if the site breakdown is valid.
   */
  if (
    !hasAdjustments(props.results, 'site') &&
    (isUndefined(subjectSiteVal) || subjectSiteVal?.le(AloftPrice.zero))
  ) {
    return null;
  }
  return (
    <SummarySection>
      <TitleAndExportToggleLockup
        {...props}
        title="Site Value & Adjustments"
        id="isSiteCommentaryInExport"
        commentary={commentaryProps}
      />
      <Typography variant="body1">
        Site values were determined through the extraction method, which involves calculating the
        estimated value of each site by deducting the depreciated cost of improvements from the
        total sale price. Extracted site values encompass raw land and the as-is value of site
        improvements like grading, drains, landscaping, paving, and utilities. Differences in site
        value can be attributed to factors such as site utility, size, location, and view. Site
        adjustments are based on the difference between the comparable property’s site value and the
        subject’s site value.
      </Typography>
      <PDFOnly>
        <Typography variant="body1">{commentaryProps.commentary}</Typography>
      </PDFOnly>
      {compChunks.map((sequence, tableIdx) => (
        <AdjustmentBreakdownSiteValueTable
          key={`market-conditions-table-${tableIdx}`}
          sequence={sequence}
          tableIdx={tableIdx}
          {...props}
        />
      ))}
      <PairedCostSettingsBreakdown {...props} type="site" />
      <WebOnly>
        <WebFlex marginTop="1rem" width="40rem">
          <Input
            type="multiline"
            onChange={setCommentary}
            value={commentary}
            onClear={() => setCommentary('')}
            onBlur={() => commentaryProps.updateCommentary(commentary)}
            resize="both"
            label="Commentary"
            size="sm"
          />
        </WebFlex>
      </WebOnly>
    </SummarySection>
  );
};
